import { CustomerAddressModelType } from '@teinor/erp/types/company/customer/customerAddress'
import { SellShippingModelType, SellShippingTypePriceInversed } from '@teinor/erp/types/company/order/sell/sellShipping'
import { useState, useMemo, useCallback, useEffect } from 'react'
import CustomError from '../../../../../../../../baseComponents/CustomError'
import { permissionCheck } from '../../../../../../../../baseComponents/PermissionChecker/function'
import InputTableLeftColumnForm from '../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/InputTableLeftColumnForm'
import { InputTableLeftTabsProps, InputTableLeftColumnChange } from '../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/types'
import { SetCompleteDataTy, OrderHeaderURLs } from '../../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { DropDownSortFunct } from '../../../../../../../../forms/CustomSelect/DropDown'
import { emptyExtraLineFirstSortFunction } from '../../../../../../../../forms/CustomSelect/functions'
import { FormElement, FormRender } from '../../../../../../../../forms/FormRenderer/types'
import { twinFetchPostJSON } from '../../../../../../../../utils/globals/data'
import { TwinDictionary, dictionaryComplexFromJsonArr } from '../../../../../../../../utils/globals/dictionary'
import { getCurrencySymbol, prepareVatTag } from '../../../../../../../../utils/globals/price'
import { returnClassOfSpanTemplate } from '../../../../../../../../utils/globals/tailwind'
import useOrderShippingTypePrice from '../../../../../../../../utils/hooks/orders/useOrderShippingTypePrice'
import useOrderTypeShippingDict from '../../../../../../../../utils/hooks/orders/useOrderTypeShippingDict'
import useTwinTranslation from '../../../../../../../../utils/hooks/useTwinTranslation'
import { getUserPermissions } from '../../../../../../../../utils/reducers/getters'
import { displayFullNameService, parseServicesFromDictShippingCompanies, parseDictCustomerAddress } from '../../functions'
import { SellShippingModelTypeExtended } from '../types'
import '../sellHeader.sass'
import { SellOrderTypeShippingInversed } from '@teinor/erp/types/company/order/sell/sellOrder'


type SellStepHeaderShippingProps = InputTableLeftTabsProps & SellStepHeaderShippingLogicProps & {
}

const SellStepHeaderShipping: React.FC<SellStepHeaderShippingProps> = ({ handleChange, headerData, getCompleteData, setCompleteData, orderHeaderURLs, modelId}) => {
    const { t } = useTwinTranslation()
    const { dictTypeShipping, dictCustomerAddress, dictShippingServices, deciderHandleChange, dynamicFormData } = useSellStepHeaderShippingLogic({ setCompleteData, headerData, handleChange, getCompleteData, orderHeaderURLs, modelId })
    const dictTypePrice = useOrderShippingTypePrice()
    const extraElements: FormElement<keyof SellShippingModelType>[] = []
    if (dynamicFormData.showAddress) {
        extraElements.push({
            name: 'ShippingAddressId',
            label: t('shippingAddress', 'Dirección de envio'),
            component: 'CustomSelectWithSearchBar',
            items: dictCustomerAddress || {},
            required: true,
            value: headerData?.OrderShipping?.ShippingAddressId || '',
            className: returnClassOfSpanTemplate(6)
        })
    }
    if (dynamicFormData.showErrorShippingService) {
        extraElements.push({
            name: 'ShippingServiceId',
            component: 'Custom',
            render: () => <CustomError className={returnClassOfSpanTemplate(6)} textError={t('notShippingServicesAvailableForThisAddress', 'No hay servicios de transportista disponibles para esta dirección')} />
        })
    } else if (dynamicFormData.showInfo) {
        extraElements.push({
            name: 'ShippingServiceId',
            component: 'CustomSelectBase',
            items: dictShippingServices,
            label: t('shippingServices', 'Servicio de Transportista'),
            displayTextFunc: displayFullNameService,
            DropDownComponent: DropDownSortFunct,
            value: headerData?.OrderShipping?.ShippingServiceId,
            className: returnClassOfSpanTemplate(6),
            extraDropDownParams: {
                sortFunction: parseServicesFromDictShippingCompanies
            }
        },
            {
                name: 'customerPays',
                label: t('postageDue', 'Portes debidos'),
                component: 'CheckboxMargin',
                checked: headerData?.OrderShipping?.customerPays,
                className: returnClassOfSpanTemplate(3)
            },
            {
                name: 'numberPackages',
                label: t('numberPackages', 'Nº bultos'),
                component: 'InputNumberWithLabelMargin',
                value: headerData?.OrderShipping?.numberPackages,
                className: returnClassOfSpanTemplate(3),
            },
            {
                name: 'weight',
                component: 'InputNumberWithLabelMarginTextRight',
                label: t('weight', 'Peso'),
                textRight: t('kg', 'kg'),
                value: headerData.OrderShipping.weight,
                classNameParent: returnClassOfSpanTemplate(3)
            })
        if (!dynamicFormData.customerPays) {
            extraElements.push({
                name: 'typePrice',
                label: t('shippingCalculationS', 'Cálculo trans.'),
                component: 'CustomSelect',
                items: dictTypePrice,
                value: headerData?.OrderShipping?.typePrice,
                className: returnClassOfSpanTemplate(3),
            })
        }
    }
    if (dynamicFormData.introducePrice) {
        extraElements.push(
            {
                name: headerData.OrderShipping.priceVatIncluded ? 'finalPriceWithTax' : 'initialPriceWithoutTax',
                component: 'InputNumberWithLabelMarginTextRight',
                label: t('priceToCustomer', 'Precio cliente'),
                textRight: dynamicFormData.textRight,
                value: dynamicFormData.price,
                classNameParent: returnClassOfSpanTemplate(4)
            }
        )
    }
    const sections: FormRender<SellShippingModelTypeExtended> = [
        {
            cols: 6,
            elements: [
                {
                    name: 'typeShipping',
                    label: t('typeShipping', 'Tipo de envío'),
                    component: 'CustomSelect',
                    items: dictTypeShipping,
                    required: true,
                    className: returnClassOfSpanTemplate(6),
                    sortFunction: emptyExtraLineFirstSortFunction
                },

                ...extraElements,

            ]
        }
    ]

    return (
        <InputTableLeftColumnForm handleChange={deciderHandleChange} form={sections} headerData={headerData} />
    )
}

interface SellStepHeaderShippingLogicProps {
    setCompleteData: SetCompleteDataTy
    headerData: TwinDictionary
    handleChange: InputTableLeftColumnChange
    getCompleteData: () => void
    orderHeaderURLs: OrderHeaderURLs
    modelId: string
}

export const useSellStepHeaderShippingLogic = ({ setCompleteData, headerData, handleChange, getCompleteData, orderHeaderURLs, modelId }: SellStepHeaderShippingLogicProps) => {
    const [dictCustomerAddress, setDictCustomerAddress] = useState<TwinDictionary | null>(null)
    const dictShippingServices = useMemo(() => dictionaryComplexFromJsonArr(headerData?.OrderShipping?.ShippingServices || [], 'ShippingServiceId'), [headerData?.OrderShipping?.ShippingServices])
    const dynamicFormData = useDynamicFormData(headerData, dictShippingServices)
    const dictTypeShipping = useOrderTypeShippingDict()
    const CustomerId = headerData?.CustomerId || ''

    const getAllCustomerAddress = useCallback(async () => {
        const items: CustomerAddressModelType[] | false = await twinFetchPostJSON('/api/app/customer/customerAddress/getAllCustomerAddresses', {
            where: { 'CustomerId': CustomerId }
        })
        parseDictCustomerAddress(items, setDictCustomerAddress)
    }, [setDictCustomerAddress, CustomerId])

    const handleChangeSellShipping: InputTableLeftColumnChange = useCallback(async (id: any, field: string, value: string | number) => {
        if ((field === 'initialPriceWithoutTax' || field === 'weight' || field === 'finalPriceWithTax') && value === '') {
            value = 0
        }
        setCompleteData((old: any) => {
            if (!old) {
                return null
            }
            const myData = JSON.parse(JSON.stringify(old))
            myData.header['OrderShipping'] = { ...myData.header['OrderShipping'], [field]: value }
            return { ...myData }
        })
        await twinFetchPostJSON(orderHeaderURLs.updateShipping, {
            id,
            [modelId]: headerData?.id,
            [field]: value,
            'WorkingCenterId': headerData?.WorkingCenterId
        }, {})

    }, [setCompleteData, headerData?.WorkingCenterId, headerData.id, orderHeaderURLs.updateShipping, modelId])

    const handleCreateSellShipping: InputTableLeftColumnChange = useCallback(async (id: any, field: string, value: any) => {
        const WorkingCenterId = field === 'ShippingAddressId' ? { 'WorkingCenterId': headerData?.WorkingCenterId } : undefined
        const result = await twinFetchPostJSON(orderHeaderURLs.createShipping, { [modelId]: headerData?.id, [field]: value, ...WorkingCenterId, CurrencyId: headerData.CurrencyId })
        if (result) {
            setCompleteData((old: any) => {
                if (!old) {
                    return null
                }
                const myData = JSON.parse(JSON.stringify(old))
                myData.header['OrderShipping'] = result
                myData.header['OrderShippingId'] = result.id
                return { ...myData }
            })
        }
    }, [setCompleteData, headerData, orderHeaderURLs.createShipping, modelId])

    const deciderHandleChange: InputTableLeftColumnChange = useCallback(async (id, field, value) => {
        if (field !== 'typeShipping') {
            if (headerData?.OrderShipping?.id) {
                await handleChangeSellShipping(headerData?.OrderShippingId, field, value)
            } else {
                await handleCreateSellShipping(headerData?.OrderShippingId, field, value)
            }
            if (field === 'ShippingAddressId' || field === 'ShippingServiceId') {
                getCompleteData()
            }
        } else {

            await handleChange(id, field, value)
            setCompleteData((old) => {
                if (!old) {
                    return null
                }
                const myData = JSON.parse(JSON.stringify(old))
                myData.header['OrderShipping'] = undefined
                myData.header['OrderShippingId'] = undefined
                return { ...myData }
            })
        }

    }, [handleChangeSellShipping, handleCreateSellShipping, headerData, setCompleteData, getCompleteData, handleChange])


    useEffect(() => {
        if (permissionCheck(getUserPermissions('customer.profile.address'), 'read')) {
            getAllCustomerAddress()
        }
    }, [getAllCustomerAddress])


    return { dictTypeShipping, dictCustomerAddress, deciderHandleChange, dictShippingServices, dynamicFormData }
}

const useDynamicFormData = (headerData: TwinDictionary, dictShippingServices: TwinDictionary) => {
    const { t } = useTwinTranslation()
    return {
        showInfo: headerData?.OrderShipping?.ShippingAddressId !== undefined && headerData?.OrderShipping?.ShippingAddressId && Object.keys(dictShippingServices)?.length && headerData?.typeShipping !== null,
        textRight: getCurrencySymbol(headerData.CurrencyId) + ' - ' + prepareVatTag(headerData?.OrderShipping?.priceVatIncluded, t),
        price: headerData?.OrderShipping?.priceVatIncluded ? headerData?.OrderShipping?.finalPriceWithTax : headerData?.OrderShipping?.initialPriceWithoutTax,
        showAddress: parseInt(headerData?.typeShipping) === SellOrderTypeShippingInversed['sendToCustomer'],
        showErrorShippingService: Object.keys(dictShippingServices)?.length && dictShippingServices[headerData?.OrderShipping?.ShippingServiceId]?.ShippingService['ShippingRate'] ? undefined : headerData?.OrderShipping?.id,
        introducePrice: parseInt(headerData?.OrderShipping?.typePrice) === SellShippingTypePriceInversed['introducePrice'],
        customerPays: headerData?.OrderShipping?.customerPays
    }
}

export default SellStepHeaderShipping